import React, { useCallback } from 'react';
import { Clock } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

// Reusable components
import ReportWidget from './components/ReportWidget';
import CustomTooltip from './components/CustomTooltip';
import { formatDurationHours } from '../../constants/durationUtils';
import { useTranslation } from 'react-i18next';

/**
 * HoursDataPoint - Data point display with hours suffix
 */
const HoursDataPoint = ({ value, label, color, subtitle, onClick }) => (
  <div
    className="text-center"
    onClick={onClick}
    style={{ cursor: onClick ? 'pointer' : 'default' }}
  >
    <h3 className="mb-2 fw-bold" style={{ fontSize: '1.75rem' }}>
      {value}
    </h3>
    <span
      className="px-3 py-1 rounded-pill d-inline-block text-center"
      style={{
        border: `2px solid ${color}`,
        color: color,
        fontSize: '0.8rem',
        fontWeight: 500,
        minWidth: '90px'
      }}
    >
      {label}
    </span>
    {subtitle && (
      <p className="text-muted mt-1 mb-0" style={{ fontSize: '0.7rem' }}>
        {subtitle}
      </p>
    )}
  </div>
);

/**
 * Summary row displaying Total Hours, AVG Hours, and MTTR AVG Hours
 */
const SummaryRow = ({
  totalHours,
  avgHours,
  mttrAvgHours,
  onTotalHoursClick,
  onAvgHoursClick,
  onMttrClick
}) => {
  const { t } = useTranslation();
  return (
  <div className="d-flex flex-wrap justify-content-center justify-content-md-end align-items-start gap-3 gap-md-5 mb-3 py-2">
    <HoursDataPoint
      value={formatDurationHours(totalHours, { t, compact: true })}
      label={t('shared.total_hours', 'Total Hours')}
      color="#10b981"
      onClick={onTotalHoursClick}
    />

    <div className="border-end d-none d-md-block" style={{ height: '60px' }} />

    <HoursDataPoint
      value={formatDurationHours(avgHours, { t, compact: true })}
      label={t('shared.avg_hours', 'AVG Hours')}
      color="#10b981"
      onClick={onAvgHoursClick}
    />

    <div className="border-end d-none d-md-block" style={{ height: '60px' }} />

    <HoursDataPoint
      value={formatDurationHours(mttrAvgHours, { t, compact: true })}
      label={t('shared.mttr_avg_hours', 'MTTR AVG Hours')}
      color="#3b82f6"
      subtitle={t('reporting.time_to_complete.mttr_subtitle', 'Only on Non-Repeating Work Orders')}
      onClick={onMttrClick}
    />
  </div>
);
}

/**
 * TimeToCompleteWidget - Displays time to complete metrics and trends
 *
 * Shows:
 * - Summary: Total Hours, AVG Hours, MTTR AVG Hours
 * - Line chart showing AVG Hours and MTTR trends over time
 * - Status duration breakdown: Time in Open, Pending, In Progress, On Hold
 */
/**
 * Status duration summary cards below main summary
 */
const StatusDurationRow = ({ summary }) => {
  const { t } = useTranslation();
  const statuses = [
    { key: 'avgSubmittedHours', label: t('shared.avg_open', 'Avg Open'), color: '#34d399' },
    { key: 'avgPendingHours', label: t('shared.avg_pending', 'Avg Pending'), color: '#f59e0b' },
    { key: 'avgInProgressHours', label: t('shared.avg_in_progress', 'Avg In Progress'), color: '#8b5cf6' },
    { key: 'avgOnHoldHours', label: t('shared.avg_on_hold', 'Avg On Hold'), color: '#ef4444' },
  ];

  const hasData = statuses.some(s => summary?.[s.key] != null);
  if (!hasData) return null;

  return (
    <div className="d-flex flex-wrap justify-content-center gap-3 mb-3 pt-2 border-top">
      {statuses.map(({ key, label, color }) => (
        <div key={key} className="text-center px-2">
          <div className="fw-bold" style={{ fontSize: '1.1rem', color }}>
            {summary?.[key] != null ? formatDurationHours(summary[key], { t, compact: true }) : '—'}
          </div>
          <div className="text-muted" style={{ fontSize: '0.7rem' }}>{label}</div>
        </div>
      ))}
    </div>
  );
};

const TimeToCompleteWidget = ({
  data,
  loading,
  error,
  onTotalHoursClick,
  onAvgHoursClick,
  onMttrClick,
  onChartPointClick,
  onDetailsClick
}) => {
  const { t } = useTranslation();
  const { summary, chartData } = data || {};
  const { totalHours = 0, avgHours = 0, mttrAvgHours = 0 } = summary || {};
  const isEmpty = !chartData || chartData.length === 0;

  // Handle chart dot click for MTTR line
  const handleMttrDotClick = useCallback((data) => {
    if (onChartPointClick && data && data.payload) {
      onChartPointClick(data.payload, 'MTTR');
    }
  }, [onChartPointClick]);

  return (
    <ReportWidget
      title={t('reporting.time_to_complete.title', 'Time to Complete')}
      loading={loading}
      onDetailsClick={onDetailsClick}
      error={error}
      isEmpty={isEmpty}
      emptyIcon={Clock}
      emptyMessage={t('reporting.time_to_complete.empty', 'No completed work orders in the selected period.')}
    >
      {/* Summary Data Points */}
      <SummaryRow
        totalHours={totalHours}
        avgHours={avgHours}
        mttrAvgHours={mttrAvgHours}
        onTotalHoursClick={onTotalHoursClick}
        onAvgHoursClick={onAvgHoursClick}
        onMttrClick={onMttrClick}
      />

      {/* Chart */}
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData || []}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="periodLabel"
            stroke="#666"
            tick={{ fontSize: 12 }}
          />
          <YAxis stroke="#666" tick={{ fontSize: 12 }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          {/* Average Hours (green) */}
          <Line
            type="monotone"
            dataKey="avgHours"
            stroke="#10b981"
            strokeWidth={3}
            dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6 }}
            name={t('shared.avg_hours', 'AVG Hours')}
          />
          {/* MTTR line (blue) */}
          <Line
            type="monotone"
            dataKey="mttrAvgHours"
            stroke="#3b82f6"
            strokeWidth={3}
            dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4, cursor: 'pointer' }}
            activeDot={{ r: 6, cursor: 'pointer', onClick: handleMttrDotClick }}
            name="MTTR"
          />
          {/* Status duration breakdown lines */}
          <Line
            type="monotone"
            dataKey="avgSubmittedHours"
            stroke="#34d399"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
            name={t('shared.open', 'Open')}
            connectNulls
          />
          <Line
            type="monotone"
            dataKey="avgPendingHours"
            stroke="#f59e0b"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
            name={t('shared.pending', 'Pending')}
            connectNulls
          />
          <Line
            type="monotone"
            dataKey="avgInProgressHours"
            stroke="#8b5cf6"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
            name={t('shared.in_progress', 'In Progress')}
            connectNulls
          />
          <Line
            type="monotone"
            dataKey="avgOnHoldHours"
            stroke="#ef4444"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
            name={t('shared.on_hold', 'On Hold')}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>

      {/* Status Duration Summary */}
      <StatusDurationRow summary={summary} />
    </ReportWidget>
  );
};

export default TimeToCompleteWidget;
